import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('6 caractères minimum');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate(localStorage.getItem('pending_gift_token') ? `/cadeau/${localStorage.getItem('pending_gift_token')}` : '/menu');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lato-screen min-h-screen bg-[#FAF9F6] flex flex-col px-6 py-8">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="p-2 -ml-2 text-gray-400 hover:text-[#F79AAE] transition-colors w-fit"
        aria-label="Retour"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-light text-gray-800">Créer un compte</h1>
          <div className="mt-3 w-8 h-[1px] bg-[#F79AAE] mx-auto"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Prénom */}
          <div>
            <label htmlFor="register-first-name" className="block text-sm text-gray-500 mb-2">Prénom</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="register-first-name"
                name="first_name"
                type="text"
                autoComplete="given-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="Votre prénom"
                data-testid="register-first-name-input"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="register-email" className="block text-sm text-gray-500 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="register-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="votre@email.com"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="register-password" className="block text-sm text-gray-500 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="register-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="••••••••"
                required
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                data-testid="register-toggle-password"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-500 font-light" data-testid="password-requirements">6 caractères minimum</p>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="register-password-confirmation" className="block text-sm text-gray-500 mb-2">Confirmer</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="register-password-confirmation"
                name="password_confirmation"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="••••••••"
                required
                data-testid="register-confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                aria-label={showConfirmPassword ? 'Masquer la confirmation du mot de passe' : 'Afficher la confirmation du mot de passe'}
                data-testid="register-toggle-confirm-password"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-400 text-center">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#F79AAE] text-white rounded-xl text-sm hover:bg-[#e8868d] transition-colors disabled:opacity-50"
          >
            {loading ? 'Création...' : 'Créer mon compte'}
          </button>
        </form>

        {/* Login link */}
        <p className="mt-8 text-center text-sm text-gray-500">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-[#F79AAE]">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
